import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Globe, Key, Shield, Tag } from 'lucide-react'
import { apiConfigSchema, type APIFormData } from '@/schemas/tool-config'

interface APIConfigFormProps {
  defaultValues?: Partial<APIFormData>
  onValuesChange?: (values: APIFormData, isValid: boolean) => void
}

export function APIConfigForm({ defaultValues, onValuesChange }: APIConfigFormProps) {
  const { t } = useTranslation('tools')

  const form = useForm<APIFormData>({
    resolver: zodResolver(apiConfigSchema),
    defaultValues: {
      base_url: defaultValues?.base_url || '',
      api_key: defaultValues?.api_key || '',
      auth_header: defaultValues?.auth_header || 'Authorization',
      auth_prefix: defaultValues?.auth_prefix || 'Bearer',
    },
    mode: 'onChange',
  })

  const { register, formState: { errors, isValid } } = form

  // Notify parent when values change
  const handleChange = () => {
    const values = form.getValues()
    onValuesChange?.(values, isValid)
  }

  return (
    <div className="space-y-4">
      {/* Base URL */}
      <div className="space-y-2">
        <Label className="text-gray-200 flex items-center gap-2">
          <Globe className="h-4 w-4" />
          {t('config.baseUrl')}
          <span className="text-red-400">*</span>
        </Label>
        <Input
          {...register('base_url', { onChange: handleChange })}
          placeholder="https://api.example.com/v1"
          className="bg-gray-800 border-gray-600 focus:border-cyan-500/50"
        />
        {errors.base_url && (
          <p className="text-sm text-red-400">{errors.base_url.message}</p>
        )}
      </div>

      {/* API Key (optional) */}
      <div className="space-y-2">
        <Label className="text-gray-200 flex items-center gap-2">
          <Key className="h-4 w-4" />
          {t('config.apiKey')}
          <span className="text-gray-500 text-xs">({t('config.optional')})</span>
        </Label>
        <Input
          {...register('api_key', { onChange: handleChange })}
          type="password"
          placeholder="sk-..."
          className="bg-gray-800 border-gray-600 focus:border-cyan-500/50"
        />
        {errors.api_key && (
          <p className="text-sm text-red-400">{errors.api_key.message}</p>
        )}
      </div>

      {/* Auth Header */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-gray-200 flex items-center gap-2">
            <Shield className="h-4 w-4" />
            {t('config.authHeader')}
          </Label>
          <Input
            {...register('auth_header', { onChange: handleChange })}
            placeholder="Authorization"
            className="bg-gray-800 border-gray-600 focus:border-cyan-500/50"
          />
          {errors.auth_header && (
            <p className="text-sm text-red-400">{errors.auth_header.message}</p>
          )}
        </div>

        {/* Auth Prefix */}
        <div className="space-y-2">
          <Label className="text-gray-200 flex items-center gap-2">
            <Tag className="h-4 w-4" />
            {t('config.authPrefix')}
          </Label>
          <Input
            {...register('auth_prefix', { onChange: handleChange })}
            placeholder="Bearer"
            className="bg-gray-800 border-gray-600 focus:border-cyan-500/50"
          />
          {errors.auth_prefix && (
            <p className="text-sm text-red-400">{errors.auth_prefix.message}</p>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-500">
        {t('config.authHint')}
      </p>
    </div>
  )
}

export { type APIFormData }
